import { Button, Card, TextArea } from '@cimpress/react-components';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { get, post, postWithResponse } from '../../clients/AuthClient';
import { LabelSheetResponse } from '../../types/label-sheet-response';
import toast from 'react-hot-toast';
import { AcrobatchResponse } from '../../types/acrobatch-response';

function TestLabelSheetsWithGangs({tenant, lineLabelConfigId}:
  {
    tenant: string,
    lineLabelConfigId: string,
  }): ReactElement {

  const [gangNbrText, setGangNbrText] = useState('');
  const [labelSheetResponses, setLabelSheetResponses] = useState<LabelSheetResponse[]>();
  const [acrobatchResponses, setAcrobatchResponses] = useState<Record<string, AcrobatchResponse | undefined>>({});

  const gangNbrs = gangNbrText
    ? gangNbrText.split(/\D+/).map(p => parseInt(p.trim())).filter(n => isFinite(n))
    : [];

  useEffect(() => {
    const interval = setInterval(() => {
      const newResponses = {...acrobatchResponses};
      const requestsToCheck = Object.entries(acrobatchResponses).filter(([, request]) => !request || request.status === 'pending');
      if (requestsToCheck.length === 0) {
        return;
      }
      const tasks = requestsToCheck.map(async ([id]) => {
        const result = await get<AcrobatchResponse>(`${process.env.REACT_APP_ACROBATCH_URL}/v1/poll-result/${id}`);
        newResponses[id] = result;
        return result;
      });
      toast.promise(Promise.all(tasks), {
        loading: 'Polling Acrobatch for results...',
        error: e => `An error occurred while polling Acrobatch: ${e.message}`,
      }).then(() => setAcrobatchResponses(newResponses));
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [acrobatchResponses]);

  const generateLabelSheets = useCallback(() => {
    setLabelSheetResponses([]);
    setAcrobatchResponses({});
    toast.promise(postWithResponse<any, LabelSheetResponse[]>(`${process.env.REACT_APP_LABEL_SHEET_SERVICE_URL}/v1/tenant/${tenant}/labelSheets/testPrintRequest`, {
      lineLabelConfigId,
      gangNbrs,
    })
      .then(data => {
        setLabelSheetResponses(data);
        const newAcrobatchResponses: Record<string, AcrobatchResponse | undefined> = {};
        data.forEach(r => {
          newAcrobatchResponses[r.acrobatchRequestId] = undefined;
        });
        setAcrobatchResponses(newAcrobatchResponses);
      }),
    {
      loading: 'Generating label sheets',
      error: e => `An error occurred while generating label sheets: ${e.message}`,
      });
  }, [gangNbrs, lineLabelConfigId, tenant]);

  const printResult = (labelSheetId: string, acrobatchResult: AcrobatchResponse) => {
    toast.promise(post<AcrobatchResponse>(`${process.env.REACT_APP_LABEL_SHEET_SERVICE_URL}/v1/tenant/${tenant}/labelSheets/callback/${labelSheetId}?isTestMode=true`, acrobatchResult),
    {
      loading: 'Printing label sheet',
      error: e => `An error occurred while printing the label sheet: ${e.message}`,
    });
  };

  return <>
    <div>
      This testing tool generates PDFs with this line label config with the gangs you supply.
    </div>
    <div>
      <strong>Notes:</strong>
      <ul>
        <li>The statuses of the gangs/items will NOT be affected.</li>
        <li>This process skips the filters used by normal label sheets (so line number, current status, etc., will be ignored). For example, you could test with already printed gangs.</li>
        <li>After generating label sheets, you can also print them. This uses the same mechanism of the normal process, so you can test that part of the process as well.</li>
        <li>Since status is not affected, any printed labels could be duplicates of real labels that could be printed later. These testing labels should be discarded when done.</li>
      </ul>
    </div>
    <hr/>
    <TextArea
      value={gangNbrText}
      onChange={r => setGangNbrText(r.target.value)}
      helpText={<>Please enter the gang numbers you want to test with (separated by new-lines, commas, etc.). {gangNbrs.length > 0 ? <strong>Parsed gangNbrs: {gangNbrs.join(', ')}</strong> : <></>}</>}
      size={10}
    />
    <Button onClick={generateLabelSheets} disabled={gangNbrs.length < 1}>Generate Label Sheets</Button>

    {labelSheetResponses && <>
      <hr />
      <h3>Responses</h3>
      {labelSheetResponses.map((response, index) => {
        const acrobatchResponse = acrobatchResponses[response.acrobatchRequestId];
        const pdfUrl = acrobatchResponse && acrobatchResponse.status === 'completed' ? acrobatchResponse.results[0].outputUrl : undefined;
        return <Card key={index}  header={<>Response {index + 1}</>} style={{marginBottom: 5}}>
          <div><strong>Label Sheet ID:</strong> {response.labelSheetId}</div>
          <div><strong>Acrobatch ID:</strong> {response.acrobatchRequestId}</div>
          <div><strong>PDF URL:</strong> {pdfUrl ? <a href={pdfUrl} target='_blank' rel="noreferrer">{pdfUrl}</a> : acrobatchResponse?.status ?? 'pending' }</div>
          <br />
          <div><Button disabled={!pdfUrl} onClick={() => acrobatchResponse && printResult(response.labelSheetId, acrobatchResponse)}>Print PDF</Button></div>
        </Card>;
      })}
    </>}
  </>;

}

export default TestLabelSheetsWithGangs;

